import { Info } from '@crehana/compass.icons.outline/v2/info';
import { List } from '@crehana/compass.icons.outline/v2/list';
import { Refresh } from '@crehana/compass.icons.outline/v2/refresh';
import { Button } from '@crehana/compass.ui/v3/button';
import { AlertDialog, Dialog } from '@crehana/compass.ui/v3/dialog';
import { BasicTooltip, TooltipProvider } from '@crehana/compass.ui/v3/tooltip';
import { BasicTopBanner } from '@crehana/compass.ui/v3/top-banner';

import { useTranslation } from '../i18n';
import { formatDateTime, formatMarkTime } from '../utils';

type TMarkInfo = {
  // ISO timestamp, we should get the date and the time from this
  markedAt: string;
  shiftName: string;
  markEventLabel: string;
  // the configured time for the mark to be registered
  // this can have a different date than the markedAt, for example: marketAt is 05:00 but expectedAt is 06:00
  markExpectedAt: string;
};

type TMarkResultDialogProps = {
  variant: 'alert' | 'success' | 'warning';
  onRetry?: () => void;
  retryLoading?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  markInfo?: TMarkInfo;
  bannerMessage?: string;
  timeZone: string;
};

export const AftRecordMarkDialog = ({
  variant,
  onOpenChange,
  open,
  markInfo,
  bannerMessage,
  onRetry,
  retryLoading,
  timeZone,
}: TMarkResultDialogProps) => {
  const { t } = useTranslation();

  const renderContent = () => {
    if (!variant || !bannerMessage || !markInfo) {
      return null;
    }

    const { dateStr, timeStr } = formatDateTime({
      isoDateStr: markInfo.markedAt,
      languageCode: 'es',
      timeZone,
    });

    return (
      <>
        <BasicTopBanner
          variant={variant}
          message={bannerMessage}
          hideCloseButton
          className="tw-px-1.6"
        />

        <Dialog.Body>
          <div className="tw-flex tw-flex-col tw-gap-1.6">
            <div className="tw-flex tw-flex-col">
              <DetailRow label={t('registered_mark_modal_row_day')}>
                {dateStr}
              </DetailRow>

              <DetailRow label={t('registered_mark_modal_row_shift')}>
                {markInfo.shiftName ??
                  t('registered_mark_modal_row_without_shift')}
              </DetailRow>

              <DetailRow label={t('registered_mark_modal_row_event')}>
                {markInfo.markEventLabel}
              </DetailRow>

              <div className="tw-flex tw-justify-between tw-items-center tw-py-2">
                <span className="tw-cds-font-body-02 tw-font-semibold tw-cds-text-primary tw-flex tw-items-center tw-gap-0.4">
                  {t('registered_mark_modal_row_time')}
                  <TooltipProvider>
                    <BasicTooltip
                      content={t('registered_mark_modal_row_tooltip')}
                      side="top"
                    >
                      <button
                        type="button"
                        className="tw-flex"
                        aria-label={t('registered_mark_modal_row_tooltip')}
                      >
                        <Info size={20} />
                      </button>
                    </BasicTooltip>
                  </TooltipProvider>
                </span>

                <div className="tw-flex tw-flex-col tw-items-end tw-gap-0.8">
                  <span className="tw-cds-font-body-02 tw-cds-text-primary">
                    {timeStr}
                  </span>
                  {markInfo.markExpectedAt && (
                    <span className="tw-cds-font-body-01 tw-cds-text-secondary">
                      {t('registered_mark_modal_row_estimated_time')}:{' '}
                      {formatMarkTime(markInfo.markExpectedAt, timeZone)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Dialog.Body>

        {variant === 'alert' && (
          <Dialog.Footer>
            <Dialog.Close
              render={
                <Button type="button" variant="tertiary">
                  {t('cancelar')}
                </Button>
              }
            />
            <Button
              type="button"
              variant="primary"
              startIcon={Refresh}
              onClick={onRetry}
              loading={retryLoading}
            >
              {t('registered_mark_modal_row_retry')}
            </Button>
          </Dialog.Footer>
        )}
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="sm">
        <Dialog.Header variant="row">
          <Dialog.Icon
            config={{
              icon: List,
              variant: 'default',
            }}
            size="sm"
          />
          <Dialog.Title>{t('registered_mark_modal_title')}</Dialog.Title>
          <Dialog.Description srOnly>
            {t('registered_mark_modal_title')}
          </Dialog.Description>
          <Dialog.CloseButton />
        </Dialog.Header>
        {renderContent()}
      </Dialog.Content>
    </Dialog>
  );
};

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="tw-flex tw-justify-between tw-items-start tw-py-2 tw-border-b tw-cds-border-color-default tw-border-solid last:tw-border-b-0">
      <span className="tw-cds-font-subtitle-01">{label}</span>
      <span className="tw-cds-font-body-02 tw-text-right">{children}</span>
    </div>
  );
};

type TBeforeRecordMarkDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading?: boolean;
  description?: string;
  title: string;
  onConfirm?: () => void;
};

export const BeforeRecordMarkDialog = ({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  loading,
}: TBeforeRecordMarkDialogProps) => {
  const { t } = useTranslation();

  return (
    <AlertDialog
      open={open}
      onOpenChange={onOpenChange}
      icon="warning"
      title={title}
      description={description}
      confirmAction={{
        label: t('dialog_button_continue'),
        onClick: onConfirm,
        loading,
        variant: 'warning',
      }}
      cancelAction={{
        label: t('dialog_button_cancel'),
        onClick: () => {
          onOpenChange(false);
        },
      }}
    />
  );
};
